<template>
  <div ref="root">
    <div class="form-section group collapsable">
      <span class="icon"><icon-colored-network /></span>
      {{ capitalize($t('names.network', { n: 2 })) }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          @click="clear">
          <icon-delete />
          {{ capitalize($t('actions.remove')) }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <form-label for-i-d="edit_lineitem_network">{{ capitalize($t('names.network', { n: 2 })) }} </form-label>
          <div class="col-sm-8">
            <multiselect
              v-model="selectedNetworks"
              :placeholder="capitalize($t('messages.anyNetworks'))"
              :options="availableNetworks"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="false"
              label="name"
              track-by="id"
              :class="{ 'is-danger': $validator.messageBag.errors.has('networks') }"
              :searchable="true"
              :group-values="multiselectGroupValues"
              :group-label="multiselectGroupLabels"
              :show-labels="false"
              select-label=""
              deselect-label="✕"
              selected-label="" />
            <span
              v-show="$validator.messageBag.errors.has('networks')"
              class="help is-danger"
              >{{ $validator.messageBag.errors.first('networks') }}</span
            >
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import FormLabel from '@/common/components/controls/FormLabel';
  import IconColoredNetwork from '@/common/icons/colored/Network';
  import { IconDelete } from '@/common/icons/monochrome';
  import { Creative } from '@/common/models';
  import { areArraysIdentical } from '@/globals';
  import { useNetworks } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  export default {
    components: {
      FormLabel,
      IconColoredNetwork,
      IconDelete,
    },
    props: {
      creative: {
        type: Creative,
        default: null,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['cleared', 'changed'],
    data() {
      return {
        selectedNetworks: [],
        isMounted: false,
        networksQuery: useNetworks(),
      };
    },
    computed: {
      networks() {
        return this.networksQuery.items;
      },
      availableNetworks() {
        return this.networks;
      },
      multiselectGroupValues() {
        if (this.creative.mediaOwnerIDs == null) {
          return null;
        }
        return this.creative.mediaOwnerIDs.length > 1 ? 'networks' : null;
      },
      multiselectGroupLabels() {
        if (this.creative.mediaOwnerIDs == null) {
          return null;
        }
        return this.creative.mediaOwnerIDs.length > 1 ? 'mediaOwner' : null;
      },
    },
    watch: {
      selectedNetworks(val) {
        const ids = val.map((x) => x.id).sort();
        if (areArraysIdentical([...this.creative.networkIDs].sort(), ids)) {
          return;
        }
        this.updateCreativeNetworkIDs({ creative: this.creative, value: ids });
        this.$emit('changed', val);
      },
    },
    async mounted() {
      this.isMounted = true;
      await waitForPendingQueries([this.networksQuery]);
      this.setupSelectedNetworks();
    },
    unmounted() {
      this.isMounted = false;
    },
    methods: {
      ...mapActions(['updateCreativeNetworkIDs']),
      capitalize,
      setupSelectedNetworks() {
        const selectedNetworks = this.networks.filter((x) => this.creative.networkIDs.some((y) => y === x.id));
        this.selectedNetworks = selectedNetworks;
      },
      clear() {
        this.updateCreativeNetworkIDs({ creative: this.creative, value: [] });
        this.$emit('cleared');
      },
    },
  };
</script>
